import type { ReactNode } from "react";
import { StyleSheet, View } from "react-native";

type Column = "main" | "rail";

// HR-11, PG-57: the wide split of a widget surface (Home, Progress). MAIN takes
// two thirds, the RAIL one third capped at 380 so its compact cards do not
// balloon on an ultra-wide screen. Each column keeps the athlete's one order.
// All rail widgets hidden: main takes the whole width. Narrow: one list, the
// host's own flow. `stacked`: the rail under the main, so a main widget gets
// the whole width (Home's hero with a program preview open, #358). Same tree,
// only styles change: nothing in it remounts.
export function WidgetColumns<Id extends string>({
  order,
  columnOf,
  wide,
  renderItem,
  gap,
  railTopInset,
  stacked = false,
}: {
  order: readonly Id[];
  columnOf: (id: Id) => Column;
  wide: boolean;
  /** Returns a keyed node, or null for a widget with nothing to show. */
  renderItem: (id: Id) => ReactNode;
  /** Between the two columns and between the cards in each. Home's cards
   *  carry their own margins and pass none. */
  gap?: number;
  /** Home lines the rail's first card up with the hero's top margin. */
  railTopInset?: number;
  stacked?: boolean;
}) {
  if (!wide) return <View style={{ gap }}>{order.map(renderItem)}</View>;
  const main = order.filter((id) => columnOf(id) === "main");
  const rail = order.filter((id) => columnOf(id) === "rail");
  return (
    <View style={[widgetColumnStyles.cols, stacked && stack.cols, { gap }]}>
      <View style={[widgetColumnStyles.main, stacked && stack.box, { gap }]}>{main.map(renderItem)}</View>
      {rail.length > 0 ? (
        <View style={[widgetColumnStyles.rail, stacked && stack.box, { gap, paddingTop: railTopInset }]}>
          {rail.map(renderItem)}
        </View>
      ) : null}
    </View>
  );
}

/** The one split style: the widgets above and the config sheet's two lists. */
export const widgetColumnStyles = StyleSheet.create({
  // flex-start so the columns don't stretch to equal height.
  cols: { flexDirection: "row", alignItems: "flex-start" },
  main: { flex: 2, minWidth: 0 },
  rail: { flex: 1, minWidth: 0, maxWidth: 380 },
});

// One column, each box as tall as its content and as wide as the row.
const stack = StyleSheet.create({
  cols: { flexDirection: "column", alignItems: "stretch" },
  box: { flexGrow: 0, flexShrink: 0, flexBasis: "auto" },
});
